import React, { FC, PropsWithChildren } from "react";
import { Link } from "../link";
import Image from "next/image";
import { HeaderNav } from "./header-nav";
import type { HeaderTranslations } from "@/translations";
import type { PostPreview } from "@/types";
import { SearchResults } from "./search-results";
import { searchPosts } from "@/util/client/search";
import { debounce } from "lodash";
import { useRouter } from "next/router";

interface Props {
  data: HeaderTranslations;
}

export const Header: FC<PropsWithChildren<Props>> = ({ data }) => {
  const [showMobileNav, setShowMobileNav] = React.useState(false);
  const [showSearch, setShowSearch] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState("");
  const searchInputDOM = React.useRef<HTMLInputElement>(null);
  const [searchResults, setSearchResults] = React.useState<
    PostPreview[] | null
  >(null);
  const router = useRouter();
  const searchDOM = React.useRef<HTMLDivElement>(null);

  const handleSearch = () => {
    searchPosts(searchValue)
      .then((items) => {
        setSearchResults(items);
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  };

  const handleSearchResultsClickOutside = (event: MouseEvent) => {
    const results = document.querySelector(".searchResults") as HTMLElement;
    const search = searchDOM.current;

    if (results && search) {
      if (
        !results.contains(event.target as Node) &&
        !search.contains(event.target as Node)
      ) {
        setShowSearch(false);
      }
    }
  };

  const debouncedHandleSearch = debounce(handleSearch, 300);

  React.useEffect(() => {
    setSearchResults(null);
    setSearchValue("");

    if (showSearch) {
      const input = searchInputDOM.current;
      if (input) {
        input.focus();
      }
      document.addEventListener("click", handleSearchResultsClickOutside);
    } else {
      document.removeEventListener("click", handleSearchResultsClickOutside);
    }
  }, [showSearch]);

  React.useEffect(() => {
    debouncedHandleSearch();
    return () => {
      debouncedHandleSearch.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  React.useEffect(() => {
    if (showMobileNav) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [showMobileNav]);

  const hideMobileNav = () => {
    setShowMobileNav(false);
    setShowSearch(false);
  };

  React.useEffect(() => {
    router.events.on("routeChangeStart", hideMobileNav);

    return () => {
      router.events.off("routeChangeStart", hideMobileNav);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <header className={`header ${showSearch ? "searching" : ""}`}>
      <div className="wrapper">
        <Link
          noStyle
          href={data.home.link?.uri || "#"}
          aria-label="Home"
          className="header--logo"
          onClick={() => {
            setShowMobileNav(false);
          }}
        >
          <Image src="/logo.svg" alt="Logo" width={117} height={38} />
        </Link>
        <div className="header--main">
          <HeaderNav data={data} showOnMobile={showMobileNav} />
          <div className="header--actions">
            {/* <div className="header--lang">
              <button
                className="header--lang-toggler"
                aria-label={data.languageSwitch.text}
              >
                <span>EN</span>
                <svg
                  viewBox="0 0 8 4"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1 1L4 3.06026L7 1"
                    stroke="black"
                    strokeWidth="1.5"
                  />
                </svg>
              </button>
            </div> */}
            <div ref={searchDOM} className="header--search">
              <button
                className="header--search-toggle"
                aria-label={data.search.text}
                onClick={() => {
                  setShowSearch(!showSearch);
                }}
              >
                <svg
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7.22346 13.7606C10.6606 13.7606 13.4469 10.9622 13.4469 7.51018C13.4469 4.05817 10.6606 1.25977 7.22346 1.25977C3.78634 1.25977 1 4.05817 1 7.51018C1 10.9622 3.78634 13.7606 7.22346 13.7606Z"
                    stroke="#020611"
                    strokeWidth="1.5"
                  />
                  <path
                    d="M15.0012 15.3217L11.6172 11.9219"
                    stroke="#020611"
                    strokeWidth="1.5"
                  />
                </svg>
              </button>
              <input
                ref={searchInputDOM}
                type="search"
                placeholder="Search"
                value={searchValue}
                onChange={(event) => {
                  setSearchValue(event.target.value);
                }}
                className="header--search-input"
              />
              <button
                className="header--search-hide"
                onClick={() => {
                  if (!searchValue) {
                    setShowSearch(false);
                  } else {
                    setSearchValue("");
                  }
                }}
                aria-label="Clear search"
              >
                <svg
                  width="12"
                  height="13"
                  viewBox="0 0 12 13"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g opacity="0.5">
                    <path
                      d="M11 1.53516L1 11.7788"
                      stroke="black"
                      strokeWidth="2"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M1 1.53516L11 11.7788"
                      stroke="black"
                      strokeWidth="2"
                      strokeLinejoin="round"
                    />
                  </g>
                </svg>
              </button>
            </div>
            <button
              className="header--menuToggle"
              aria-label="Toggle menu"
              onClick={() => {
                setShowMobileNav(!showMobileNav);
              }}
            >
              {showMobileNav ? (
                <svg
                  viewBox="0 0 14 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M13 1L1 13"
                    stroke="#020611"
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M1 1L13 13"
                    stroke="#020611"
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                <svg
                  viewBox="0 0 12 10"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M0 5H12" stroke="#020611" strokeWidth="1.5" />
                  <path d="M0 1H12" stroke="#020611" strokeWidth="1.5" />
                  <path d="M0 9H12" stroke="#020611" strokeWidth="1.5" />
                </svg>
              )}
            </button>
          </div>
          {searchResults && (
            <SearchResults
              searchValue={searchValue}
              results={searchResults}
              resultsCount={searchResults.length}
              translations={data.searchResults}
            />
          )}
        </div>
      </div>
    </header>
  );
};
